/**
 * Overview summary cards (TMMPDM-0052). Each card links to the leaf it describes,
 * and loads, fails and retries on its own. The card's error boundary wraps that
 * whole card, including its read and its loading and failed states.
 * Figures are server fields or the length of a list the server returns in full.
 * Settlements are newest-first: the card shows the first row of each currency
 * (`netMinor`), never a sum. Unpaid charges are not shown — no per-reseller total
 * is published, and summing charge rows would compose one.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Card, CardActionArea, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { PanelErrorBoundary } from '@/components/PanelErrorBoundary';
import { ErrorState } from '@/components/page';
import { Amount } from '@/components/ui';
import { formatMoney, formatMonthYear } from '@/format';
import { adminApi } from '@/api/tmm/admin';
import type { PayoutStatementView, ResellerStandingState } from '@/api/tmm/types';
import { RESELLER_STANDING_STATES } from '@/api/tmm/types';
import { StandingChip } from './StandingChip';

const MEANING_FALLBACK: Record<ResellerStandingState, string> = {
  GOOD: 'It is meeting its minimum.',
  PROBATION: 'It missed its minimum and is on probation.',
  DEACTIVATION_DUE: 'It missed its minimum through probation.',
  DEACTIVATED: 'Its administrators lose access; its tenant keeps selling.',
};

function isKnownState(state: string): state is ResellerStandingState {
  return (RESELLER_STANDING_STATES as readonly string[]).includes(state);
}

/** First row of each currency, in the server's newest-first order. Does not sum. */
export function latestSettlementPerCurrency(
  rows: readonly PayoutStatementView[],
): PayoutStatementView[] {
  const seen = new Set<string>();
  const latest: PayoutStatementView[] = [];
  for (const row of rows) {
    if (seen.has(row.currency)) continue;
    seen.add(row.currency);
    latest.push(row);
  }
  return latest;
}

interface ReadState<T> {
  data: T | null;
  failure: unknown;
  loading: boolean;
  reload: () => void;
}

export function useRead<T>(resellerId: number, load: (id: number) => Promise<T>): ReadState<T> {
  const loadRef = useRef(load);
  // Inline loaders are new each render. Keep the latest one without refetching on that identity.
  useEffect(() => {
    loadRef.current = load;
  });
  const [token, setToken] = useState(0);
  const requestKey = `${resellerId}:${token}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    data: T | null;
    failure: unknown;
  }>({ requestKey: null, data: null, failure: null });

  useEffect(() => {
    let cancelled = false;
    loadRef
      .current(resellerId)
      .then((data) => {
        if (!cancelled) setResponse({ requestKey, data, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setResponse({ requestKey, data: null, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, requestKey]);

  const current = response.requestKey === requestKey;
  return {
    data: current ? response.data : null,
    failure: current ? response.failure : null,
    loading: !current,
    reload: () => setToken((n) => n + 1),
  };
}

export function CardFrame({
  to,
  title,
  children,
}: {
  to: string;
  title: string;
  children: ReactNode;
}) {
  const location = useLocation();
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea
        component={RouterLink}
        to={to}
        state={location.state}
        sx={{ height: '100%', alignItems: 'stretch' }}
      >
        <CardContent>
          <Typography variant="subtitle2" component="h2" gutterBottom>
            {title}
          </Typography>
          {children}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export function CardShell({ children }: { children: ReactNode }) {
  return (
    <PanelErrorBoundary>
      <Box sx={{ height: '100%' }}>{children}</Box>
    </PanelErrorBoundary>
  );
}

export function LoadingCard({ title }: { title: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" component="h2" gutterBottom>
          {title}
        </Typography>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </CardContent>
    </Card>
  );
}

export function FailedCard({
  title,
  failure,
  onRetry,
  extra,
}: {
  title: string;
  failure: unknown;
  onRetry: () => void;
  extra?: ReactNode;
}) {
  const { t } = useCopy();
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" component="h2" gutterBottom>
          {title}
        </Typography>
        <ErrorState
          error={failure}
          message={t('resellers.overview.card.loadError', "Couldn't load this.")}
          onRetry={onRetry}
        />
        {extra}
      </CardContent>
    </Card>
  );
}

export function StandingSummaryCard(props: {
  state: string | null;
  loading: boolean;
  failure: unknown;
  onRetry: () => void;
  extra?: ReactNode;
}) {
  return (
    <CardShell>
      <StandingSummaryBody {...props} />
    </CardShell>
  );
}

function StandingSummaryBody({
  state,
  loading,
  failure,
  onRetry,
  extra,
}: {
  state: string | null;
  loading: boolean;
  failure: unknown;
  onRetry: () => void;
  extra?: ReactNode;
}) {
  const { t } = useCopy();
  const title = t('resellers.overview.card.standing', 'Standing');
  if (failure) {
    return (
      <FailedCard
        title={title}
        failure={failure}
        onRetry={onRetry}
        {...(extra != null ? { extra } : {})}
      />
    );
  }
  return (
    <CardFrame to="?tab=standing" title={title}>
      <Stack spacing={0.5}>
        {loading || state === null ? (
          <Skeleton variant="text" width="50%" />
        ) : (
          <>
            <Box>
              <StandingChip state={state} />
            </Box>
            {isKnownState(state) ? (
              <Typography variant="body2" color="text.secondary">
                {t(`resellers.overview.card.meaning.${state}`, MEANING_FALLBACK[state])}
              </Typography>
            ) : null}
          </>
        )}
        {extra}
      </Stack>
    </CardFrame>
  );
}

export function LatestSettlementCard({ resellerId }: { resellerId: number }) {
  return (
    <CardShell>
      <LatestSettlementBody resellerId={resellerId} />
    </CardShell>
  );
}

function LatestSettlementBody({ resellerId }: { resellerId: number }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const title = t('resellers.overview.card.latestSettlement', 'Latest settlement');
  const read = useRead(resellerId, (id) => adminApi.listResellerSettlements(id));
  if (read.failure)
    return <FailedCard title={title} failure={read.failure} onRetry={read.reload} />;
  if (read.loading || !read.data) return <LoadingCard title={title} />;
  // A non-list body is this card's failure, not a throw that takes the Overview alerts with it.
  const statements = read.data.payoutStatements;
  if (!Array.isArray(statements))
    return <FailedCard title={title} failure={read.data} onRetry={read.reload} />;
  const latest = latestSettlementPerCurrency(statements);
  return (
    <CardFrame to="?tab=settlements" title={title}>
      {latest.length === 0 ? (
        <Typography variant="body2">{t('resellers.overview.card.none', 'None')}</Typography>
      ) : (
        <Stack spacing={0.5}>
          {latest.map((row) => (
            <Typography key={row.id} variant="body2">
              {formatMonthYear(row.periodStart, i18n.language)}
              {' · '}
              {row.payoutStatus === 'RETAINED' ? (
                <>
                  {t('resellers.settlements.outcome.RETAINED', 'Retained')}
                  {' · '}
                </>
              ) : null}
              {row.retainedPart || row.normalPart ? (
                <>
                  {row.retainedPart
                    ? t('resellers.settlements.retainedPart', 'Retained {{amount}}', {
                        amount: formatMoney(row.retainedPart.netMinor, row.currency, i18n.language),
                      })
                    : null}
                  {row.retainedPart && row.normalPart ? ' · ' : null}
                  {row.normalPart
                    ? t('resellers.settlements.normalPart', 'Payable {{amount}}', {
                        amount: formatMoney(
                          row.normalPart.payableMinor,
                          row.currency,
                          i18n.language,
                        ),
                      })
                    : null}
                </>
              ) : (
                <Amount minor={row.netMinor} currency={row.currency} />
              )}
            </Typography>
          ))}
        </Stack>
      )}
    </CardFrame>
  );
}

async function affiliateSummary(resellerId: number) {
  const [plans, affiliates] = await Promise.all([
    adminApi.listCommissionPlans(resellerId),
    adminApi.listResellerAffiliates(resellerId),
  ]);
  return {
    affiliates: affiliates.affiliates.length,
    plans: plans.commissionPlans.length,
  };
}

export function AffiliatesSummaryCard({ resellerId }: { resellerId: number }) {
  return (
    <CardShell>
      <AffiliatesSummaryBody resellerId={resellerId} />
    </CardShell>
  );
}

function AffiliatesSummaryBody({ resellerId }: { resellerId: number }) {
  const { t } = useCopy();
  const title = t('partners.tab.affiliates', 'Affiliates');
  const read = useRead(resellerId, affiliateSummary);
  if (read.failure)
    return <FailedCard title={title} failure={read.failure} onRetry={read.reload} />;
  if (read.loading || !read.data) return <LoadingCard title={title} />;
  return (
    <CardFrame to="?tab=affiliates" title={title}>
      <Typography variant="body2">
        {t(
          'resellers.overview.card.affiliateAssigned',
          '{{plans, number}} plans · {{affiliates, number}} affiliates assigned',
          { plans: read.data.plans, affiliates: read.data.affiliates },
        )}
      </Typography>
    </CardFrame>
  );
}

export function OverviewCardGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))',
        mb: 3,
      }}
    >
      {children}
    </Box>
  );
}
